import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, FileCheck, Shield, Search, Target, Package, AlertCircle, Award } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AmazonListingSEO = () => {
  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Amazon Product Listing & SEO Services | Expert Cataloging India</title>
        <meta name="description" content="Professional Amazon product listing with keyword-rich titles, bullet points & backend keywords. Increase visibility & sales with SEO-optimized listings." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/amazon-product-listing-seo-optimization" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/amazon-account-management-services" className="text-slate-700 hover:text-primary font-medium">Amazon</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Product Listing & SEO</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Amazon Product Listing & <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">SEO Optimization Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Professional Amazon Approval Support & High-Converting SEO Listings for Sustainable Growth.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we help Amazon sellers resolve approval challenges and create powerful, SEO-optimized listings that improve organic visibility, increase conversions, and drive long-term sales growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=amazon-listing-seo">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Listing Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=amazon-listing-seo">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to SEO Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Approval Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Amazon Approval Services
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto">
              If your listings are restricted or require category approval, we provide complete documentation support and structured submission assistance to reduce rejection risks and speed up approval.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* 1. Grocery & Gourmet Foods Approval */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold">Grocery & Gourmet Foods Approval</h3>
              </div>
              <p className="text-slate-600 mb-6">
                We assist sellers in obtaining category approval by ensuring proper documentation and compliance.
              </p>
              <div className="space-y-3">
                {[
                  'FSSAI License verification',
                  'Live product images submission',
                  'Amazon-generated declaration letter support',
                  'Documentation review as per Amazon guidelines'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 2. Brand Approval & Brand Registry Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold">Brand Approval & Brand Registry Support</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-800 font-semibold mb-3">If you are a Brand Owner:</p>
                <div className="space-y-2 mb-4">
                  {[
                    'Assistance with Amazon Brand Registry application',
                    'Trademark-based brand approval support',
                    'Complete documentation guidance'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-slate-800 font-semibold mb-3">If you are NOT the Brand Owner:</p>
                <div className="space-y-2">
                  {[
                    'Brand approval via live product images',
                    'Minimum 6 images required with visible brand name',
                    'Packaging & branding compliance verification'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* 3. Battery Product Approval */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold">Battery Product Approval</h3>
              </div>
              <p className="text-slate-600 mb-6">
                For Lithium, AAA, or rechargeable battery products:
              </p>
              <div className="space-y-3">
                {[
                  'Safety Data Sheet (SDS) management',
                  'Lithium battery compliance documentation',
                  'Hazardous material document alignment',
                  'Verification of battery type requirements'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 4. Topicals Category Approval */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-200"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold">Topicals Category Approval</h3>
              </div>
              <p className="text-slate-600 mb-4">
                For shampoo, skincare, face creams, and cosmetic products. Ensure packaging clearly mentions:
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Manufacturer name & full address',
                  'Marketed by details',
                  'Shelf life',
                  'Expiry date',
                  'Batch number'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 italic">
                We assist with live image verification and structured approval submission.
              </p>
            </motion.div>
          </div>

          {/* Important Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 max-w-4xl mx-auto"
          >
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start space-x-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 font-medium">
                Incomplete packaging details may lead to rejection. We verify everything before applying.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Listing Optimization & New Listing Creation */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Search className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Listing Optimization & New Listing Creation
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto">
              Getting approval is only the first step. We create SEO-friendly, conversion-focused Amazon listings designed to increase organic visibility and improve sales performance.
            </p>
          </motion.div>

          {/* Our Optimization Process */}
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
                Our Optimization Process
              </h3>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Keyword-Optimized Titles */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle2 className="w-7 h-7 text-green-500" />
                  <h4 className="font-heading text-xl font-bold">Keyword-Optimized Titles</h4>
                </div>
                <p className="text-slate-600">
                  High-search-volume keyword integration while maintaining Amazon compliance.
                </p>
              </motion.div>

              {/* SEO-Optimized Bullet Points */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle2 className="w-7 h-7 text-green-500" />
                  <h4 className="font-heading text-xl font-bold">SEO-Optimized Bullet Points</h4>
                </div>
                <p className="text-slate-600">
                  Clear, benefit-driven bullets that improve readability and conversions.
                </p>
              </motion.div>

              {/* Structured Product Descriptions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle2 className="w-7 h-7 text-green-500" />
                  <h4 className="font-heading text-xl font-bold">Structured Product Descriptions</h4>
                </div>
                <p className="text-slate-600">
                  Engaging descriptions that reduce confusion and increase buyer confidence.
                </p>
              </motion.div>

              {/* Backend Search Term Optimization */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle2 className="w-7 h-7 text-green-500" />
                  <h4 className="font-heading text-xl font-bold">Backend Search Term Optimization</h4>
                </div>
                <p className="text-slate-600">
                  Strategic keyword placement to improve discoverability and ranking.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Objective */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Target className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-8">
              Our Objective
            </h2>
            <p className="text-xl text-slate-300 mb-10">
              To create impressive, professionally structured listings where:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                'Content is clear and easy to understand',
                'Customers are never confused',
                'Organic visibility improves',
                'Conversion rate increases',
                'Long-term sales growth is achieved'
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm p-5 rounded-xl border border-white/20"
                >
                  <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-3" />
                  <p className="font-medium">{item}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Amazon Brands We Currently Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We optimize listings for 20+ brands across diverse categories, helping them achieve better visibility and conversions.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Brand</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Product / Focus</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { brand: 'Nutri Hut', product: 'FMCG / Nutrition Products', category: 'FMCG / Food & Nutrition' },
                  { brand: 'Rayon Caboki', product: 'Hair/Beauty Products', category: 'Beauty & Personal Care / Hair Care' },
                  { brand: 'Ayurdaanta Herbs', product: 'Ayurvedic Herbs & Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Ayur Devam', product: 'Ayurvedic/FMCG Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Suparna Trikha', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Haute', product: 'Gas Stoves', category: 'Home & Kitchen / Kitchen Appliances' },
                  { brand: 'GK Trading', product: 'Wallpapers', category: 'Home Improvement / Home Décor' },
                  { brand: 'Yuri Woori', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Mooncntrl', product: 'Apparel', category: 'Fashion / Apparel' },
                  { brand: 'Indian Kitchen Foods', product: 'Ready-to-Eat Meals', category: 'FMCG / Packaged Food & Ready-to-Eat' },
                  { brand: 'Earthvya', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'GW Khamkar', product: 'FMCG Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Vintage Oils', product: 'Oils / Edible Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Fitelo', product: 'Smart Weighing Scales', category: 'Electronics / Smart Health Devices' },
                  { brand: 'Fitty', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'RajGharana Metals', product: 'Kitchen Utensils', category: 'Home & Kitchen / Cookware & Kitchenware' },
                  { brand: 'HII Health Inside It', product: 'Health & Wellness Products', category: 'Health & Wellness' },
                  { brand: 'Auli Lifestyle', product: 'Skincare & Beauty Products', category: 'Beauty & Personal Care / Skincare' },
                  { brand: 'EHOP', product: 'TV & Electronic Remotes', category: 'Electronics / Consumer Electronics Accessories' },
                  { brand: 'Lavenust', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                ].map((item, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.brand}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.product}</td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {item.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Improve Your Amazon Listings?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our experts handle your approvals and optimize your listings for maximum visibility and performance.
            </p>
            <Link to="/contact?service=amazon-listing-seo">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Listing Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonListingSEO;
